'use client';

import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faRoute } from '@fortawesome/pro-solid-svg-icons';
import { EmptyState, CircularProgress } from 'src/components/PulseUI';

import {
  useGetAllRoutesQuery,
  useDeleteRouteMutation,
  Route,
} from 'src/api/redux/routes';
import { useRouteActions } from 'src/hooks/routes';
import {
  toggleRouteVisibility,
  selectRouteVisibility,
  selectPendingProvider,
} from 'src/redux';
import { endSession } from 'src/redux/slices/routeNavigationSlice';
import { useAppSelector } from 'src/redux/hooks';
import { DeleteRouteDialog } from 'src/components/Routes/components/DeleteRouteDialog';
import { RouteRow } from './components';
import { PendingProviderBanner } from '../RoutesDrawer';

/**
 * RouteListPanel Component
 *
 * Panel showing saved routes with actions to view, edit, delete, and toggle visibility.
 * Designed to fit in a sidebar or collapsible section of the Strategy Center layout.
 */
export interface RouteListPanelProps {
  /** Optional className for additional styling */
  className?: string;
}

const RouteListPanel: React.FC<RouteListPanelProps> = ({ className = '' }) => {
  const dispatch = useDispatch();
  const { data: routes, isLoading, isError } = useGetAllRoutesQuery();
  const { startEdit, startView } = useRouteActions();
  const [deleteRouteMutation] = useDeleteRouteMutation();
  const routeVisibility = useSelector(selectRouteVisibility);
  const pendingProvider = useAppSelector(selectPendingProvider);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState<Route | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEdit = useCallback(
    (routeId: number) => {
      // Find the route to get its label
      const route = routes?.find((r) => r.id === routeId);
      startEdit({
        routeId: String(routeId),
        label: route?.label || '',
        stops: [], // Will be populated by RoutePanel when routeStops loads
      });
    },
    [startEdit, routes]
  );

  const handleView = useCallback(
    (routeId: number) => {
      // A pending provider is added to the route as it opens for editing
      if (pendingProvider) {
        handleEdit(routeId);
        return;
      }
      const route = routes?.find((r) => r.id === routeId);
      startView({
        routeId: String(routeId),
        label: route?.label || '',
        stops: [], // The detail panel loads the stops
      });
    },
    [pendingProvider, handleEdit, startView, routes]
  );

  const handleDeleteClick = useCallback((route: Route) => {
    setRouteToDelete(route);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!routeToDelete) return;

    setIsDeleting(true);
    try {
      await deleteRouteMutation(String(routeToDelete.id)).unwrap();
      dispatch(endSession(routeToDelete.id));
      setDeleteDialogOpen(false);
      setRouteToDelete(null);
    } catch (error) {
      console.error('Failed to delete route:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [routeToDelete, deleteRouteMutation, dispatch]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setRouteToDelete(null);
  }, []);

  const handleToggleVisibility = useCallback(
    (routeId: number) => {
      dispatch(toggleRouteVisibility(String(routeId)));
    },
    [dispatch]
  );

  // Loading state
  if (isLoading) {
    return (
      <div
        className={`flex flex-col items-center justify-center p-8 ${className}`}
        data-testid="route-list-panel-loading"
      >
        <CircularProgress size="md" color="primary" />
        <span className="mt-3 text-sm text-neutral-500">Loading routes...</span>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className={`p-4 ${className}`} data-testid="route-list-panel-error">
        <EmptyState
          title="Unable to load routes"
          description="There was an error loading your routes. Please try again later."
          size="sm"
          className="bg-transparent border-none"
        />
      </div>
    );
  }

  // Empty state
  if (!routes || routes.length === 0) {
    return (
      <div className={`p-4 ${className}`} data-testid="route-list-panel-empty">
        <EmptyState
          title="No routes yet"
          description="Create your first route to plan and optimize your visits to healthcare providers."
          icon={<Icon icon={faRoute} className="text-sky-500 text-3xl" />}
          size="sm"
          className="bg-transparent border-none"
        />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col ${className}`}
      data-testid="route-list-panel"
    >
      {/* Pending Provider Banner */}
      <PendingProviderBanner />
      {/* Route List */}
      <div className="flex flex-col gap-2 p-4 overflow-y-auto">
        <span
          className="text-xs font-medium text-neutral-500"
          data-testid="route-list-count"
        >
          {routes.length} {routes.length === 1 ? 'route' : 'routes'}
        </span>
        {routes.map((route) => (
          <RouteRow
            key={route.id}
            route={route}
            isVisible={routeVisibility[String(route.id)] ?? false}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={() => handleDeleteClick(route)}
            onToggleVisibility={handleToggleVisibility}
          />
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteRouteDialog
        isOpen={deleteDialogOpen}
        routeLabel={routeToDelete?.label || ''}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default RouteListPanel;
